import { create } from "zustand";

interface EditorState {
    isDirty: boolean;
    wordCount: number;
    isSaving: boolean;
    loreRefreshKey: number;
    /** AI badge dismissals — survives Lore↔Assistant tab switching so dismissed
     *  badges don't flash back on remount before the next DB fetch lands. */
    dismissedAiBadges: Set<string>;
    setDirty: (dirty: boolean) => void;
    setWordCount: (count: number) => void;
    setSaving: (saving: boolean) => void;
    bumpLoreRefresh: () => void;
    dismissAiBadgeId: (entryId: string) => void;
}

export const useEditorStore = create<EditorState>((set) => ({
    isDirty: false,
    wordCount: 0,
    isSaving: false,
    loreRefreshKey: 0,
    dismissedAiBadges: new Set(),
    setDirty: (dirty) => set({ isDirty: dirty }),
    setWordCount: (count) => set({ wordCount: count }),
    setSaving: (saving) => set({ isSaving: saving }),
    bumpLoreRefresh: () => set((s) => ({ loreRefreshKey: s.loreRefreshKey + 1 })),
    dismissAiBadgeId: (entryId) =>
        set((s) => ({
            dismissedAiBadges: new Set([...s.dismissedAiBadges, entryId]),
        })),
}));

/** Wait for an already-started chapter save before reading server context. */
export function waitForPendingEditorSave(): Promise<void> {
    if (!useEditorStore.getState().isSaving) return Promise.resolve();

    return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            unsubscribe();
            resolve();
        };
        const unsubscribe = useEditorStore.subscribe((state) => {
            if (!state.isSaving) finish();
        });

        // Close the subscribe-after-check race if the save completed between
        // the first read and listener registration.
        if (!useEditorStore.getState().isSaving) finish();
    });
}
